﻿import React from 'react';
import { GridColDef, GridRowModel, useGridSelector, GridFooterContainerProps, gridRowCountSelector, gridRowSelectionCountSelector, gridExpandedRowCountSelector, gridPaginationSelector, useGridApiContext, useGridRootProps, GridFooterContainer, GridSelectedRowCount, GridRowCount } from '@mui/x-data-grid-pro';
import { TertiaryButton } from "../../../shared/components";
import CloudDownload from "@mui/icons-material/CloudDownload";
import { utils, writeFile } from "xlsx";
import moment from "moment";

interface SCGridFooterContainerProps extends GridFooterContainerProps {
    hideExportToExcel?: boolean;
    additionalFooterItems?: React.ReactNode;
}

type SCGridColDef = Omit<GridColDef, 'valueGetter'> & {
    valueGetter?: (value: any, row: GridRowModel, column: SCGridColDef, api: any) => any;
};

// Use https://github.com/mui-org/material-ui-x/blob/master/packages/grid/_modules_/grid/components/GridFooter.tsx as a base in order to modify the content of the Grid Footer...
export default React.forwardRef<HTMLDivElement, SCGridFooterContainerProps>(
    function GridFooter(props, ref) {
        const { hideExportToExcel, additionalFooterItems, ...rest } = props;
        const apiRef = useGridApiContext();
        const options = useGridRootProps();
        const totalRowCount = useGridSelector(apiRef, gridRowCountSelector);
        const selectedRowCount = useGridSelector(apiRef, gridRowSelectionCountSelector);
        const { paginationModel: pagination } = useGridSelector(apiRef, gridPaginationSelector);
        const visibleRowCount = useGridSelector(apiRef, gridExpandedRowCountSelector);

        const SelectedRowCountElement =
            !options.hideFooterSelectedRowCount && selectedRowCount > 0 ? (
                <GridSelectedRowCount selectedRowCount={selectedRowCount} />
            ) : (
                    <div />
                );

        const RowCountElement =
            !options.hideFooterRowCount && !options.pagination ? (
                <GridRowCount rowCount={totalRowCount} visibleRowCount={visibleRowCount} />
            ) : null;

        const PaginationComponent =
            options.pagination &&
            pagination.pageSize != null &&
            !options.hideFooterPagination &&
            options.slots?.pagination;

        const PaginationElement = PaginationComponent && (
            <PaginationComponent page {...options.slotProps?.basePagination} {...options.slotProps?.pagination} />
        );

        const onExportToExcelClicked = () => {
            const gridRows = Array.from(apiRef.current.getRowModels().entries()).map(([_, item]) => item);
            const gridColumns: SCGridColDef[] = apiRef.current.getAllColumns();

            // Map fields to column headers for each row...
            const exportRows = gridRows.map(row => {
                const result = {};
                gridColumns.forEach(col => {
                    result[col.headerName] = col.valueGetter ? col.valueGetter(row[col.field], row, col, apiRef) : row[col.field];
                });
                return result;
            });

            const wb = utils.book_new();
            const ws = utils.json_to_sheet(exportRows);
            utils.book_append_sheet(wb, ws, "Grid Data");
            const filename = `${document.title} - ${moment().format("YYYY-MM-DD-HHmm")}.xlsx`;
            writeFile(wb, filename);
        }

        const ExportToExcelComponent =
            !hideExportToExcel && (
                <TertiaryButton id="export-to-excel" color="primary" size="small" onClick={onExportToExcelClicked} title="Export Data to Excel">
                    <div className="icon-and-text"><CloudDownload /><span>XLSX</span></div>
                </TertiaryButton>
            );

        return (
            <GridFooterContainer ref={ref} {...rest}>
                <div className="MuiDataGrid-footer-custom">
                    {ExportToExcelComponent}
                    {additionalFooterItems}
                </div>
                {SelectedRowCountElement}
                {RowCountElement}
                {PaginationElement}
            </GridFooterContainer>
        );
    },
);